import { Button, Form, Input, Modal } from "antd";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { toast } from "react-toastify";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "Context/AppContext";
import { popupStyles } from "Components";
import { testImage } from "utils";
import {
  ResponseDataType,
  postRequest,
  uploadFileRequest,
  getRequest,
} from "api";
import { OrganizationInfo } from "Context/AppContext";

interface CreateOrganizationPropType {
  customerId: string;
  close: () => void;
  getOrgList: () => void;
}

interface CreateOrgDataType {
  organizationId: string;
  memberId: string;
  departmentId: string;
  logo?: string;
  avatar?: string;
}

interface OrgListDto {
  orgId?: string;
  orgList: OrganizationInfo[];
  memberId?: string;
}

function CreateOrganizationPopup(props: CreateOrganizationPropType) {
  const navigate = useNavigate();
  const location = useLocation();
  const { changeOrganization } = useAppContext();
  const hasMounted = useRef(false);
  const [form] = Form.useForm();
  const { close, customerId, getOrgList } = props;
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  const handleUploadImage = async (
    file: File,
    type: "logo" | "avatar"
  ): Promise<string> => {
    if (!file) {
      toast.error("Không có file được chọn");
      return "";
    }

    if (file.size > 5000000) {
      toast.error(`File ${file.name} vượt quá 5MB`);
      return "";
    }
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Chỉ hỗ trợ file ảnh (jpg, png)");
      return "";
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("files", file);

    try {
      const response: ResponseDataType<{
        success: boolean;
        uploadedFiles: { url: string }[];
      }> = await uploadFileRequest(
        `/files/upload-image?type=${type}`,
        formData
      );

      if (response.code !== 0) {
        toast.error(
          typeof response.message === "string"
            ? response.message
            : response.message.join(", ")
        );
        return "";
      }

      const url = response.data?.uploadedFiles?.[0]?.url;
      if (!url) {
        toast.error("Không nhận được URL từ server");
        return "";
      }
      return url;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Lỗi khi tải file lên");
      return "";
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (hasMounted.current) {
      close();
    } else {
      hasMounted.current = true;
    }
  }, [location.pathname, close]);

  const CreateOrganization = async (value: {
    name: string;
    logo: string;
    ownerName: string;
    phone?: string;
    avatar?: string;
  }) => {
    let logoUrl = value.logo;
    let avatarUrl = value.avatar;

    if (logoFile) {
      logoUrl = await handleUploadImage(logoFile, "logo");
      if (!logoUrl) {
        toast.error("Vui lòng upload lại logo");
        return;
      }
    }
    if (avatarFile) {
      avatarUrl = await handleUploadImage(avatarFile, "avatar");
      if (!avatarUrl) {
        toast.error("Vui lòng upload lại avatar");
        return;
      }
    }

    if (logoUrl && !isValidUrl(logoUrl)) {
      toast.error("URL logo không hợp lệ");
      return;
    }
    if (avatarUrl && !isValidUrl(avatarUrl)) {
      toast.error("URL avatar không hợp lệ");
      return;
    }

    Modal.confirm({
      style: { fontFamily: "Quicksand" },
      title: "Xác nhận tạo tổ chức mới?",
      zIndex: 10000,
      async onOk() {
        const response: ResponseDataType<CreateOrgDataType> = await postRequest(
          "/organization/create",
          {
            name: value.name,
            logo: logoUrl,
            ownerId: customerId,
            ownerName: value.ownerName,
            ownerPhone: value.phone,
            ownerAvatar: avatarUrl,
          }
        );

        if (response.code) {
          if (typeof response.message === "object") {
            response.message.forEach((message: string) => toast.error(message));
          } else toast.error(response.message);
        } else {
          toast.success("Tạo tổ chức thành công");
          const newOrgId = response.data?.organizationId as string;

          // Lấy danh sách tổ chức mới nhất
          const orgListResponse: ResponseDataType<OrgListDto> =
            await getRequest(
              `/customer/get-org-list?customerId=${customerId}&orgId=${newOrgId}`
            );

          if (orgListResponse.code) {
            toast.error(orgListResponse.message);
            return;
          }

          const newOrgList = orgListResponse.data?.orgList || [];
          localStorage.setItem("organizationId", newOrgId);
          changeOrganization(newOrgId, response.data?.memberId, newOrgList); // Cập nhật currentOrg, currentOrgInfo và orgList
          setLogoUrl(response.data?.logo || "");
          setAvatarUrl(response.data?.avatar || "");
          getOrgList(); // Cập nhật danh sách tổ chức trong component cha
          close(); // Đóng popup

          // Chỉ điều hướng nếu không đang ở /member-list
          if (!location.pathname.includes("/member-list")) {
            navigate(`/member-list?orgId=${newOrgId}`);
          }
        }
      },
    });
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div
      className={popupStyles.popup_wrapper}
      style={{ backgroundColor: "#FBFBFB" }}
    >
      <div
        className={popupStyles.popup_content}
        style={{ backgroundColor: "#FBFBFB" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div className={popupStyles.popup_title}>Tạo tổ chức</div>
          <div className={popupStyles.popup_close} onClick={close}>
            <CloseOutlined style={{ fontSize: "24px", color: "black" }} />
          </div>
        </div>
        <Form
          form={form}
          onFinish={CreateOrganization}
          layout="vertical"
          requiredMark={false}
        >
          <div
            className={popupStyles.form}
            style={{ border: "none", backgroundColor: "#FBFBFB" }}
          >
            <Form.Item
              label={
                <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                  Tên tổ chức
                </span>
              }
              name="name"
              rules={[{ required: true, message: "Cần nhập tên tổ chức!" }]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Tên tổ chức"
                className={popupStyles.form_input_create_org}
                style={{ padding: "10px 7px" }}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className={popupStyles.form_label}>Logo tổ chức</span>
              }
              name="logo"
              rules={[
                () => ({
                  async validator(_, value) {
                    if (logoFile) return Promise.resolve();
                    if (!value) return Promise.resolve();
                    let checkFlag;
                    try {
                      checkFlag = await testImage(value, 2000);
                    } catch (error) {
                      checkFlag = false;
                    }
                    if (checkFlag) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject("Link logo lỗi!");
                    }
                  },
                }),
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder={logoFile ? logoFile.name : "Logo tổ chức"}
                className={`${popupStyles.form_input_create_org}`}
                onChange={(e) => {
                  if (!logoFile) {
                    form.setFieldsValue({ logo: e.target.value });
                  }
                }}
                suffix={
                  <UploadOutlinedIcon
                    style={{ fontSize: "25px", cursor: "pointer" }}
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = "image/jpeg,image/png";
                      input.onchange = e => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          if (file.size > 5000000) {
                            toast.error(`File ${file.name} vượt quá 5MB`);
                            return;
                          }
                          setLogoFile(file);
                          form.setFieldsValue({ logo: "" });
                        }
                      };
                      input.click();
                    }}
                  />
                }
                style={{ padding: "10px 7px" }}
              />
            </Form.Item>
            {logoFile && (
              <div style={{ marginBottom: 16 }}>
                Đã chọn: {logoFile.name}{" "}
                <Button
                  size="small"
                  onClick={() => {
                    setLogoFile(null);
                    form.setFieldsValue({ logo: "" });
                  }}
                >
                  Xóa
                </Button>
              </div>
            )}
            <Form.Item
              label={
                <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                  Tên thành viên
                </span>
              }
              name="ownerName"
              rules={[{ required: true, message: "Cần nhập tên thành viên!" }]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Tên thành viên"
                className={popupStyles.form_input_create_org}
                style={{ padding: "10px 7px" }}
              />
            </Form.Item>  
            <Form.Item
              label={
                <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                  Số điện thoại thành viên
                </span>
              }
              name="phone"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Số điện thoại thành viên"
                className={popupStyles.form_input_create_org}
                style={{ padding: "10px 7px" }}
              />
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                  Avatar thành viên
                </span>
              }
              name="avatar"
              rules={[
                () => ({
                  async validator(_, value) {
                    if (avatarFile) return Promise.resolve();
                    if (!value) return Promise.resolve();
                    let checkFlag;
                    try {
                      checkFlag = await testImage(value, 3000);
                    } catch (error) {
                      checkFlag = false;
                    }
                    if (checkFlag) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject("Link avatar lỗi!");
                    }
                  },
                }),
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder={avatarFile ? avatarFile.name : "Avatar thành viên"}
                className={`${popupStyles.form_input_create_org}`}
                onChange={(e) => {
                  if (!avatarFile) {
                    form.setFieldsValue({ avatar: e.target.value });
                  }
                }}
                suffix={
                  <UploadOutlinedIcon
                    style={{ fontSize: "25px", cursor: "pointer" }}
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = "image/jpeg,image/png";
                      input.onchange = e => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          if (file.size > 5000000) {
                            toast.error(`File ${file.name} vượt quá 5MB`);
                            return;
                          }
                          setAvatarFile(file);
                          form.setFieldsValue({ avatar: "" });
                        }
                      };
                      input.click();
                    }}
                  />
                }
                style={{ padding: "10px 7px" }}
              />
            </Form.Item>
            {avatarFile && (
              <div style={{ marginBottom: 16 }}>
                Đã chọn: {avatarFile.name}{" "}
                <Button
                  size="small"
                  onClick={() => {
                    setAvatarFile(null);
                    form.setFieldsValue({ avatar: "" });
                  }}
                >
                  Xóa
                </Button>
              </div>
            )}
          </div>
          <div className={popupStyles.form_button_wrapper}>
            <Button
              type="primary"
              className={`${popupStyles.form_button} ${popupStyles.form_button_primary}`}
              htmlType="submit"
              loading={uploading}
            >
              Tạo tổ chức
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default CreateOrganizationPopup;
